import GaugeComponent from "react-gauge-component";
import { GaugeConfig } from "../../../../data/entities";
import { rawToPercent, percentToGaugeValue } from "../../helper/helpers";

interface LiveGaugeProps {
  currentFlawValue?: number | null;
  peakFlawValue?: number | null;
  config: GaugeConfig;
  isStale?: boolean;
}

const COLORS = {
  green: "#27ae60",
  yellow: "#f1c40f",
  red: "#c0392b",
  blue: "#0D698B",
  muted: "#6c757d",
};

export const LiveGauge: React.FC<LiveGaugeProps> = ({
  currentFlawValue,
  peakFlawValue,
  config,
  isStale = false,
}) => {
  const { cautionThreshold, criticalThreshold } = config;

  const hasCurrent = currentFlawValue != null;
  const currentPercent = hasCurrent ? rawToPercent(currentFlawValue, config) : 0;
  const peakPercent =
    peakFlawValue != null ? rawToPercent(peakFlawValue, config) : null;

  const currentGaugeValue = hasCurrent ? percentToGaugeValue(currentPercent, config) : 0;
  const peakGaugeValue =
    peakPercent != null ? percentToGaugeValue(peakPercent, config) : null;

  // Single-value mode: only peak, no current
  const singleMode = !hasCurrent && peakGaugeValue != null;

  const getStatusColor = (pct: number) => {
    if (pct >= criticalThreshold) return COLORS.red;
    if (pct >= cautionThreshold) return COLORS.yellow;
    return COLORS.green;
  };

  const peakColor =
    peakPercent != null ? getStatusColor(peakPercent) : COLORS.green;

  const showPeak = peakGaugeValue != null && peakPercent != null;

  const arcConfig = {
    width: 0.2,
    padding: 0.02,
    cornerRadius: 2,
    emptyColor: "#e8e8e8",
    subArcs: [
      { limit: 33.33, color: isStale ? "#d5d5d5" : COLORS.green },
      { limit: 66.67, color: isStale ? "#c5c5c5" : COLORS.yellow },
      { color: isStale ? "#b5b5b5" : COLORS.red },
    ],
  };

  const tickLabelsConfig = {
    type: "outer" as const,
    hideMinMax: true,
    ticks: [] as any[],
  };

  if (singleMode) {
    const singleColor = isStale ? COLORS.muted : getStatusColor(peakPercent!);
    return (
      <div className="w-100">
        <div className="live-gauge-wrapper">
          <GaugeComponent
            style={{ width: "100%" }}
            value={peakGaugeValue!}
            minValue={0}
            maxValue={100}
            arc={arcConfig}
            pointer={{
              type: "needle",
              color: singleColor,
              length: 0.8,
              width: 15,
              animationDuration: 800,
              animationDelay: 0,
            }}
            labels={{
              valueLabel: { hide: true },
              tickLabels: tickLabelsConfig,
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="w-100">
      <style>{`
        .live-gauge-wrapper .multi-pointer-1 path {
          fill: ${showPeak ? (isStale ? COLORS.muted : peakColor) : 'transparent'} !important;
        }
      `}</style>
      <div className="live-gauge-wrapper">
        <GaugeComponent
          style={{ width: "100%" }}
          value={peakGaugeValue ?? currentGaugeValue}
          minValue={0}
          maxValue={100}
          arc={arcConfig}
          pointers={[
            {
              value: currentGaugeValue,
              color: isStale ? COLORS.muted : COLORS.blue,
              type: "needle",
              width: 15,
              length: 0.8,
            },
            {
              value: peakGaugeValue ?? currentGaugeValue,
              color: showPeak ? (isStale ? COLORS.muted : peakColor) : "transparent",
              type: "needle",
              width: 5,
              length: 0.6,
            },
          ]}
          labels={{
            valueLabel: { hide: true },
            tickLabels: tickLabelsConfig,
          }}
        />
      </div>
      {/* Legend */}
      <div
        className="d-flex justify-content-center gap-3 mt-2"
        style={{
          fontSize: "0.8rem",
          opacity: showPeak ? 1 : 0,
          transition: "opacity 0.3s ease",
        }}
      >
        <span className="d-flex align-items-center gap-2 text-muted">
          <span
            style={{
              display: "inline-block",
              width: 14,
              height: 4,
              background: isStale ? COLORS.muted : COLORS.blue,
              borderRadius: 2,
            }}
          />
          Current
        </span>
        <span className="d-flex align-items-center gap-2 text-muted">
          <span
            style={{
              display: "inline-block",
              width: 14,
              height: 4,
              background: isStale ? COLORS.muted : peakColor,
              borderRadius: 2,
            }}
          />
          Peak
        </span>
      </div>
    </div>
  );
};